import { useState } from "react";
import { API, getToken } from "@/lib/api";
import { X, MapPin } from "lucide-react";

const src = (e) => `${API}/files/${e.path}?auth=${getToken()}`;
const when = (e) => new Date(e.captured_at || e.uploaded_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

// Side-by-side Before vs After photo comparison for the customer's work review.
export function BeforeAfterCompare({ evidence }) {
    const before = evidence.filter((e) => e.kind === "before");
    const after = evidence.filter((e) => e.kind === "after");
    const [bi, setBi] = useState(0);
    const [ai, setAi] = useState(0);
    const [zoom, setZoom] = useState(null);
    if (!before.length && !after.length) return null;
    const cols = [
        { kind: "before", label: "Before", items: before, idx: bi, setIdx: setBi, tone: "bg-navy" },
        { kind: "after", label: "After", items: after, idx: ai, setIdx: setAi, tone: "bg-emerald-600" },
    ];
    return (
        <div className="mt-3" data-testid="before-after-compare">
            <div className="grid grid-cols-2 gap-2">
                {cols.map((c) => <CompareColumn key={c.kind} col={c} onZoom={setZoom} />)}
            </div>
            <p className="mt-2 text-center text-[11px] text-navy/45">Tap a photo to enlarge · tap thumbnails to switch</p>
            {zoom && (
                <div data-testid="compare-zoom" className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-black/90 p-4" onClick={() => setZoom(null)}>
                    <button data-testid="compare-zoom-close" className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white"><X size={20} /></button>
                    <img src={src(zoom)} alt={zoom.kind} className="max-h-[80vh] max-w-full rounded-xl object-contain" />
                    <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-white/80">{zoom.kind} · {when(zoom)}</p>
                </div>
            )}
        </div>
    );
}

function CompareColumn({ col, onZoom }) {
    const cur = col.items[col.idx];
    return (
        <div data-testid={`compare-col-${col.kind}`}>
            <div className="relative aspect-[3/4] overflow-hidden rounded-xl border border-navy/10 bg-navy/[0.04]">
                {cur ? (
                    <button type="button" data-testid={`compare-main-${col.kind}`} onClick={() => onZoom(cur)} className="h-full w-full">
                        <img src={src(cur)} alt={col.label} className="h-full w-full object-cover" />
                    </button>
                ) : <p className="flex h-full items-center justify-center p-2 text-center text-xs text-navy/40">No {col.label} photo</p>}
                <span className={`absolute left-2 top-2 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white ${col.tone}`}>{col.label}</span>
                {cur && (
                    <span className="absolute bottom-0 left-0 right-0 flex items-center gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 pb-1.5 pt-4 text-[9px] font-semibold text-white">
                        <MapPin size={9} className={cur.location_verified ? "text-emerald-300" : "text-amber-300"} /> {cur.location_verified ? "Location ✓" : "GPS N/A"} · {when(cur)}
                    </span>
                )}
            </div>
            {col.items.length > 1 && (
                <div className="mt-1.5 flex gap-1 overflow-x-auto">
                    {col.items.map((e, i) => (
                        <button key={e.id} type="button" data-testid={`compare-thumb-${col.kind}-${i}`} onClick={() => col.setIdx(i)}
                            className={`h-10 w-10 shrink-0 overflow-hidden rounded-md border-2 transition-opacity ${i === col.idx ? "border-orange" : "border-transparent opacity-60"}`}>
                            <img src={src(e)} alt="" className="h-full w-full object-cover" />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
